import {Invoice, InvoiceListRow} from './_models'
import {formatExchangeRateDisplay} from './currencyConversion'

const normalizeCurrency = (value: unknown) => String(value || '').trim().toUpperCase()

type InvoiceMoneyRecord = Invoice | InvoiceListRow

const readFinite = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null
  }
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const roundMoney = (value: number): number => Math.round(value * 10000) / 10000

export type InvoiceMoneyPresentation = {
  originalCurrency: string
  originalAmount: number | null
  companyCurrency: string | null
  companyAmount: number | null
  sameCurrency: boolean
  hasCompanySnapshot: boolean
  exchangeRateDisplay: string | null
  primaryAmount: number | null
  primaryCurrency: string | null
  secondaryAmount: number | null
  secondaryCurrency: string | null
  auxiliaryText: string | null
}

export type InvoiceAmountPresentation = {
  primaryAmount: number | null
  primaryCurrency: string | null
  secondaryAmount: number | null
  secondaryCurrency: string | null
  sameCurrency: boolean
  exchangeRateDisplay: string | null
  auxiliaryText: string | null
}

export const resolveInvoiceOriginalCurrency = (invoice: InvoiceMoneyRecord): string =>
  normalizeCurrency(invoice?.originalCurrencyCode) || normalizeCurrency(invoice?.currency) || 'USD'

export const resolveInvoiceMoneyPresentation = (
  invoice: InvoiceMoneyRecord,
  vendorView = false
): InvoiceMoneyPresentation => {
  const originalCurrency = resolveInvoiceOriginalCurrency(invoice)
  const originalAmount = readFinite(invoice?.originalTotal ?? invoice?.amount)
  const companyCurrency = normalizeCurrency(invoice?.companyCurrencyCode) || null
  const companyAmount = readFinite(invoice?.companyTotal)
  const hasCompanySnapshot = companyCurrency != null && companyAmount != null
  const sameCurrency = hasCompanySnapshot && companyCurrency === originalCurrency
  const exchangeRateDisplay =
    String((invoice as Partial<Invoice>)?.exchangeRateDisplay || '').trim() ||
    (hasCompanySnapshot
      ? formatExchangeRateDisplay(originalCurrency, invoice?.exchangeRate, companyCurrency)
      : null)

  if (vendorView) {
    return {
      originalCurrency,
      originalAmount,
      companyCurrency,
      companyAmount,
      sameCurrency,
      hasCompanySnapshot,
      exchangeRateDisplay,
      primaryAmount: originalAmount,
      primaryCurrency: originalCurrency,
      secondaryAmount: hasCompanySnapshot && !sameCurrency ? companyAmount : null,
      secondaryCurrency: hasCompanySnapshot && !sameCurrency ? companyCurrency : null,
      auxiliaryText: hasCompanySnapshot && !sameCurrency ? exchangeRateDisplay : null,
    }
  }

  if (hasCompanySnapshot) {
    return {
      originalCurrency,
      originalAmount,
      companyCurrency,
      companyAmount,
      sameCurrency,
      hasCompanySnapshot,
      exchangeRateDisplay,
      primaryAmount: companyAmount,
      primaryCurrency: companyCurrency,
      secondaryAmount: sameCurrency ? null : originalAmount,
      secondaryCurrency: sameCurrency ? null : originalCurrency,
      auxiliaryText: sameCurrency ? null : exchangeRateDisplay,
    }
  }

  return {
    originalCurrency,
    originalAmount,
    companyCurrency: null,
    companyAmount: null,
    sameCurrency: false,
    hasCompanySnapshot: false,
    exchangeRateDisplay: null,
    primaryAmount: originalAmount,
    primaryCurrency: originalCurrency,
    secondaryAmount: null,
    secondaryCurrency: null,
    auxiliaryText: 'Company currency conversion unavailable',
  }
}

export const resolveInvoiceAmountPresentation = (
  invoice: InvoiceMoneyRecord,
  amount: unknown,
  vendorView = false
): InvoiceAmountPresentation => {
  const money = resolveInvoiceMoneyPresentation(invoice, vendorView)
  const originalAmount = readFinite(amount)

  if (originalAmount == null) {
    return {
      primaryAmount: null,
      primaryCurrency: money.primaryCurrency,
      secondaryAmount: null,
      secondaryCurrency: null,
      sameCurrency: money.sameCurrency,
      exchangeRateDisplay: money.exchangeRateDisplay,
      auxiliaryText: null,
    }
  }

  if (vendorView || !money.hasCompanySnapshot) {
    return {
      primaryAmount: originalAmount,
      primaryCurrency: money.originalCurrency,
      secondaryAmount: null,
      secondaryCurrency: null,
      sameCurrency: money.sameCurrency,
      exchangeRateDisplay: money.exchangeRateDisplay,
      auxiliaryText: vendorView ? null : money.auxiliaryText,
    }
  }

  if (money.sameCurrency) {
    return {
      primaryAmount: originalAmount,
      primaryCurrency: money.companyCurrency,
      secondaryAmount: null,
      secondaryCurrency: null,
      sameCurrency: true,
      exchangeRateDisplay: null,
      auxiliaryText: null,
    }
  }

  const exchangeRate = readFinite(invoice?.exchangeRate)
  const originalTotal = readFinite(invoice?.originalTotal ?? invoice?.amount)
  const companyTotal = readFinite(invoice?.companyTotal)
  const derivedCompanyAmount =
    exchangeRate != null && exchangeRate > 0
      ? roundMoney(originalAmount * exchangeRate)
      : originalTotal != null && originalTotal !== 0 && companyTotal != null
      ? roundMoney((originalAmount * companyTotal) / originalTotal)
      : null

  if (derivedCompanyAmount == null) {
    return {
      primaryAmount: originalAmount,
      primaryCurrency: money.originalCurrency,
      secondaryAmount: null,
      secondaryCurrency: null,
      sameCurrency: false,
      exchangeRateDisplay: money.exchangeRateDisplay,
      auxiliaryText: 'Company currency conversion unavailable',
    }
  }

  return {
    primaryAmount: derivedCompanyAmount,
    primaryCurrency: money.companyCurrency,
    secondaryAmount: originalAmount,
    secondaryCurrency: money.originalCurrency,
    sameCurrency: false,
    exchangeRateDisplay: money.exchangeRateDisplay,
    auxiliaryText: money.exchangeRateDisplay,
  }
}
